import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Typography,
  IconButton,
  Modal,
  Box,
  Button,
  Grid,
} from "@mui/material";
import {
  DocumentScanner,
  CameraAlt,
  CameraFront,
  CameraRear,
  Close,
} from "@mui/icons-material";
import { Scanner } from "@yudiel/react-qr-scanner";
import Webcam from "react-webcam";
import { toast } from "react-toastify";

interface FormTextFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpdateVIN: (vin: string) => void;
  isEditing: boolean;
  disabled?: boolean;
  capturedImages: string[];
  setCapturedImages: React.Dispatch<React.SetStateAction<string[]>>;
  radioCapturedImages: string[];
  setRadioCapturedImages: React.Dispatch<React.SetStateAction<string[]>>;
  required?: boolean;
  isPusherData?: boolean;
  inputProps?: object;
}

const FormTextField: React.FC<FormTextFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onUpdateVIN,
  isEditing,
  disabled = false,
  capturedImages,
  setCapturedImages,
  radioCapturedImages,
  setRadioCapturedImages,
  required = false,
  isPusherData,
}) => {
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [isFrontFacing, setIsFrontFacing] = useState(true);
  const isDataArmazenagemField = name === "Data Armazenagem";
  const isKMFinalField = name === "KM Final";
  const webcamRef = useRef<Webcam>(null);

  const isDisabled = isPusherData && isDataArmazenagemField ? true : disabled;

  const isNumberField = name.includes("KM") || name.includes("Ano");
  const isDateField = name.includes("Data Armazenagem");
  const isVINField = name === "V.I.N";
  const isDeliveryDateField = name === "Data de Entrega";
  const isKMCodeField = name === "KM";
  const isRadioCodeField = name === "Código de Rádio";

  const inputType =
    isPusherData && isDateField ? "text" : isDateField ? "date" : "text";

  const finalInputType =
    isKMFinalField && isNumberField
      ? "number"
      : isDeliveryDateField
        ? "datetime-local"
        : inputType;

  const minDate = isDeliveryDateField
    ? new Date().toISOString().split("T")[0]
    : undefined;

  const handleOpenScanner = () => {
    setIsScannerOpen(true);
  };

  const handleCloseScanner = () => {
    setIsScannerOpen(false);
  };

  const handleOpenCamera = () => {
    setIsCameraOpen(true);
  };

  const handleCloseCamera = () => {
    setIsCameraOpen(false);
  };

  const toggleCameraFacing = () => {
    setIsFrontFacing(!isFrontFacing);
  };

  const capture = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        if (isKMCodeField) {
          setCapturedImages((prevImages) => [...prevImages, imageSrc]);
        } else if (isRadioCodeField) {
          setRadioCapturedImages((prevImages) => [...prevImages, imageSrc]);
        }
        setIsCameraOpen(false);
        toast.success("Foto Capturada com Sucesso!");
      }
    }
  };

  const clearImages = () => {
    if (isKMCodeField) {
      setCapturedImages([]);
    } else if (isRadioCodeField) {
      setRadioCapturedImages([]);
    }
  };

  const handleError = (err: any) => {
    console.error("Error reading QR code:", err);
  };

 const handleVINChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = event.target;

  if (name === "Placa" && value.length > 7) return;

  if (name === "V.I.N") {
    const vinLimpo = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (vinLimpo.length > 17) return;

    onUpdateVIN(vinLimpo);
    return;
  }

  onChange(event);
};

  const videoConstraints = {
    facingMode: isFrontFacing ? "user" : "environment",
  };

  const sanitizeVin = (raw: string) => {
    const trimmed = (raw || "").trim();

    return trimmed.toUpperCase().replace(/[^A-Z0-9]/g, "");
  };

  return (
    <>
      <Typography ml={1} mb={-1}>
        {label}
        {required &&
          !["KM Final", "Data de Entrega", "Código de Rádio"].includes(
            name,
          ) && (
            <Typography component="span" color="error">
              *
            </Typography>
          )}
      </Typography>

      <TextField
        margin="normal"
        fullWidth
        id={name}
        placeholder={`Digite o ${label}`}
        name={name}
        value={isEditing ? value : value}
        onChange={handleVINChange}
        type={finalInputType}
        disabled={isDisabled || disabled}
        InputProps={
          isVINField
            ? {
                endAdornment: (
                  <IconButton size="small" onClick={handleOpenScanner}>
                    <DocumentScanner color="action" />
                  </IconButton>
                ),
              }
            : isKMCodeField || isRadioCodeField
              ? {
                  endAdornment: (
                    <IconButton size="small" onClick={handleOpenCamera}>
                      <CameraAlt color="action" />
                    </IconButton>
                  ),
                }
              : {}
        }
        inputProps={isDateField || isDeliveryDateField ? { min: minDate } : {}}
        sx={{
          "& .MuiInputLabel-root.Mui-focused": {
            color: "#303030",
          },
          "& .MuiInputLabel-root": {
            borderColor: "#303030",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            boxShadow: 6,
            "& fieldset": {
              borderColor: "#303030",
            },
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: "#303030",
            },
        }}
      />
      {(isKMCodeField || isRadioCodeField) && (
        <>
          <Box>
            {isKMCodeField
              ? capturedImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Captured ${index}`}
                    style={{
                      maxWidth: "80px",
                      marginTop: "10px",
                      marginLeft: "4px",
                    }}
                  />
                ))
              : radioCapturedImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Captured ${index}`}
                    style={{
                      maxWidth: "80px",
                      marginTop: "10px",
                      marginLeft: "4px",
                    }}
                  />
                ))}
          </Box>

          {((isKMCodeField && capturedImages.length > 0) ||
            (isRadioCodeField && radioCapturedImages.length > 0)) && (
            <Box textAlign="center">
              <Button
                onClick={clearImages}
                style={{ marginTop: "10px", color: "#303030" }}
              >
                Limpar Imagens
              </Button>
            </Box>
          )}
        </>
      )}
      <Modal open={isCameraOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            width: "80%",
            maxWidth: 800,
            height: "80%",
            maxHeight: 700,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={handleCloseCamera}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              backgroundColor: "red",
              color: "white",
              ":hover": {
                bgcolor: "#5c5c5c",
              },
            }}
          >
            <Close />
          </IconButton>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/png"
            videoConstraints={videoConstraints}
            style={{ width: "100%", height: "auto", marginBottom: 50 }}
          />
          <Grid container spacing={2} sx={{ mt: { xs: -13, sm: -10, md: -6 } }}>
            <Grid item xs={12} sm={6} md={6}>
              <Button
                variant="contained"
                onClick={capture}
                sx={{
                  display: "block",
                  width: "100%",
                  maxWidth: "100%",
                  backgroundColor: "#303030",
                  ":hover": {
                    bgcolor: "#5c5c5c",
                  },
                }}
              >
                Capturar Imagem
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <IconButton
                onClick={toggleCameraFacing}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  backgroundColor: "#303030",
                  color: "white",
                  ":hover": {
                    bgcolor: "#5c5c5c",
                  },
                  borderRadius: 1,
                }}
              >
                {isFrontFacing ? <CameraFront /> : <CameraRear />}
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Modal open={isScannerOpen} onClose={handleCloseScanner}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            minWidth: "60%",
            minHeight: "40%",
          }}
        >
          <IconButton
            onClick={handleCloseScanner}
            sx={{
              position: "absolute",
              zIndex: 99,
              top: 10,
              right: 10,
              backgroundColor: "red",
              color: "white",
              ":hover": {
                bgcolor: "#5c5c5c",
              },
            }}
          >
            <Close />
          </IconButton>
          <Scanner
            onResult={(text) => {
              const vinLimpo = sanitizeVin(text);

              onUpdateVIN(vinLimpo);
              handleCloseScanner();
            }}
            onError={handleError}
          />
        </Box>
      </Modal>
    </>
  );
};

export default FormTextField;
